import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from 'utils/helpers';
import { InvolveParty } from 'views/orders/charges/components/GeneralChargesTable/types';
import { formatPercent } from 'views/orders/loans/utils';

const useStyles = makeStyles((theme) => ({
  text: {
    fontSize: '0.8rem',
  },
  name: {
    fontSize: '0.8rem',
    [theme.breakpoints.down('sm')]: {
      gridColumn: '1 / -1',
    },
  },
  amount: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: 'black',
  },
  atClosing: {
    fontSize: '0.8rem',
    justifySelf: 'flex-end',
    paddingRight: '5px',
    [theme.breakpoints.down('sm')]: {
      gridColumn: '1 / -1',
      justifySelf: 'flex-start',
      paddingRight: 0,
    },
  },
  title: {
    padding: '10px 10px 0px',
    fontSize: '0.8rem',
    fontWeight: 500,
    color: 'black',
  },
  partyContainer: {
    padding: '5px 10px',
    display: 'grid',
    gridTemplateColumns: '10rem 3rem auto',
    gap: '3px 10px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr auto',
    },
  },
}));

type Props = {
  parties: InvolveParty[];
  title: string;
  onEditClick: () => void;
};

export const InvolvedPartyTable: FC<Props> = ({ parties, title, onEditClick }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box
      style={{
        border: '1px solid lightgray',
        borderRadius: '5px',
      }}
    >
      <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography className={classes.title}>{title}</Typography>
        <IconButton onClick={onEditClick} size="large">
          <FontAwesomeIcon icon="edit" size="xs" />
        </IconButton>
      </Box>
      {parties.map(
        ({
          name,
          amount,
          percent,
          at_closing_amount,
          at_closing_percent,
          before_closing_amount,
          before_closing_percent,
        }) => {
          const hasValue =
            amount > 0 ||
            percent > 0 ||
            at_closing_percent > 0 ||
            before_closing_percent > 0;

          return hasValue ? (
            <Box
              key={`${name}-${amount}-${percent}`}
              className={classes.partyContainer}
              style={{
                borderBottom: parties.length > 1 ? '1px solid lightgray' : 'none',
              }}
            >
              <Typography className={classes.name}>{name}</Typography>
              <Typography className={classes.text}>
                {percent < 1 && formatPercent(percent, 2)}
              </Typography>
              <Typography className={classes.amount}>{formatMoney(amount)}</Typography>
              {at_closing_percent > 0 && (
                <>
                  <Typography className={classes.atClosing}>{t('orders:atClosingLabel')}</Typography>
                  <Typography className={classes.text}>
                    {at_closing_percent < 1 && formatPercent(at_closing_percent, 2)}
                  </Typography>
                  <Typography className={classes.amount}>
                    {formatMoney(at_closing_amount)}
                  </Typography>
                </>
              )}
              {before_closing_percent > 0 && (
                <>
                  <Typography className={classes.atClosing}>{t('orders:beforeClosingLabel')}</Typography>
                  <Typography className={classes.text}>
                    {before_closing_percent < 1 && formatPercent(before_closing_percent, 2)}
                  </Typography>
                  <Typography className={classes.amount}>
                    {formatMoney(before_closing_amount)}
                  </Typography>
                </>
              )}
            </Box>
          ) : null;
        }
      )}
    </Box>
  );
};
